import { Ellipsis, LayoutDashboard, Users } from "lucide-react";
import { CommissionsIcon } from "../../icons/commissions-icon";
import { ListingsIcon } from "../../icons/listings-icon";
import { NAV_STROKE_LOCAL, NAV_STROKE_LUCIDE } from "../../icons/stroke";
import { showNavItem } from "../Navbar/types";

interface MobileTabBarProps {
	pathname: string;
	navbarConfig: string[];
	isSubPartner: boolean;
	isAuthorized: boolean;
	isBookingPartner: boolean;
	onNavigate: (href: string, internal?: boolean) => void;
	onOpenMore: () => void;
}

/**
 * Bottom tab bar — the primary mobile navigation, per the "mweb — 2.0" Figma
 * screens. Carries the four top-level destinations plus a "More" tab that
 * opens {@link MobileFlexDrawer}; everything else lives in that sheet.
 *
 * Rendered below `lg:` only; desktop keeps the sidebar Navbar.
 */
export function MobileTabBar({
	pathname,
	navbarConfig,
	isSubPartner,
	isAuthorized,
	isBookingPartner,
	onNavigate,
	onOpenMore,
}: MobileTabBarProps) {
	// Built unfiltered so `isMoreActive` below can tell "this screen belongs to
	// another tab" from "this tab is hidden by config".
	const allTabs = [
		{
			title: "Dashboard",
			href: "/dashboard",
			icon: LayoutDashboard,
			stroke: NAV_STROKE_LUCIDE,
			isActive:
				pathname.endsWith("/dashboard") && !pathname.includes("/dashboard/"),
			isAccessible: showNavItem("dashboard", navbarConfig),
		},
		{
			title: isBookingPartner ? "Bookings" : "Leads",
			href: "/dashboard/leads",
			icon: Users,
			stroke: NAV_STROKE_LUCIDE,
			isActive: pathname.includes("/leads"),
			isAccessible: showNavItem("leads", navbarConfig),
		},
		{
			title: "Commissions",
			href: "/dashboard/commissions",
			icon: CommissionsIcon,
			stroke: NAV_STROKE_LOCAL,
			isActive: pathname.includes("/commissions"),
			isAccessible:
				!isSubPartner &&
				isAuthorized &&
				showNavItem("commissions", navbarConfig),
		},
		{
			title: "Listings",
			href: "/dashboard/listings",
			icon: ListingsIcon,
			stroke: NAV_STROKE_LOCAL,
			isActive: pathname.includes("/listings"),
			isAccessible:
				showNavItem("listings", navbarConfig) ||
				showNavItem("white_label_listings", navbarConfig),
		},
	];
	const tabs = allTabs.filter((tab) => tab.isAccessible);

	// Everything that is not one of the primary destinations is reached through
	// the More sheet, so More carries the active state while the user is on one
	// of those screens — sub-partners, teams, settings, profile and the rest.
	const isMoreActive = !allTabs.some((tab) => tab.isActive);

	return (
		/* Figma sets a flat 8px pad; env() adds the home-indicator inset on
		   notched devices and resolves to 0 wherever the artboard applies. */
		<nav
			aria-label="Primary"
			className="flex shrink-0 items-stretch border-[#e4e4e4] border-t bg-white p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]"
		>
			{tabs.map((tab) => (
				<button
					key={tab.title}
					type="button"
					onClick={() => onNavigate(tab.href, true)}
					aria-current={tab.isActive ? "page" : undefined}
					className={`flex flex-1 flex-col items-center gap-1 py-2 ${
						tab.isActive ? "text-[#0c63f8]" : "text-[#99a1af]"
					}`}
				>
					<tab.icon className="size-[22px]" strokeWidth={tab.stroke} />
					<span
						className={`max-w-full truncate text-[11px] leading-[14px] ${
							tab.isActive ? "font-semibold" : "font-medium"
						}`}
					>
						{tab.title}
					</span>
				</button>
			))}
			<button
				type="button"
				onClick={onOpenMore}
				aria-label="Open more menu"
				aria-current={isMoreActive ? "page" : undefined}
				className={`flex flex-1 flex-col items-center gap-1 py-2 ${
					isMoreActive ? "text-[#0c63f8]" : "text-[#99a1af]"
				}`}
			>
				<Ellipsis className="size-[22px]" strokeWidth={NAV_STROKE_LUCIDE} />
				<span
					className={`text-[11px] leading-[14px] ${
						isMoreActive ? "font-semibold" : "font-medium"
					}`}
				>
					More
				</span>
			</button>
		</nav>
	);
}
